import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, Sparkles, Handshake, Search, MapPin, Heart } from "lucide-react";
import SEO from "@/components/SEO";
import { FORMS } from "@/constants/brand";
import { useTranslation } from "@/i18n/LanguageContext";

const heroImg = "https://images.unsplash.com/photo-1583847268964-b28dc8f51f92?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxiZWF1dGlmdWwlMjBjb3p5JTIwbGl2aW5nJTIwcm9vbXxlbnwwfHx8fDE3ODIyNDQ0MjR8MA&ixlib=rb-4.1.0&q=85";
const contractorImg = "https://customer-assets.emergentagent.com/job_cc2168ac-14a3-4252-9a7f-505676b149a2/artifacts/43d89r3k_For%20Homeowners.jpg";
const tradesImg = "https://customer-assets.emergentagent.com/job_cc2168ac-14a3-4252-9a7f-505676b149a2/artifacts/5mlvozaq_For%20Providers.jpeg";
const computerImg = "https://customer-assets.emergentagent.com/job_gallery-refresh-15/artifacts/hnadinyb_pexels-shvetsa-3987013.jpg";

export default function HomePage() {
    const { t } = useTranslation();
    const homeownerItems = t("home.benefits.homeownersItems");
    const providerItems = t("home.benefits.providersItems");

    return (
        <div data-testid="home-page">
            <SEO
                title={t("home.seo.title")}
                description={t("home.seo.description")}
                keywords="women-owned home services, women-owned contractors, female contractors, women in trades, home services directory, trusted home service providers, women homeowners, local women-owned services"
                path="/"
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "Organization",
                    name: "TrustHer Home",
                    description: "Directory connecting women homeowners with women-owned home service providers.",
                    url: typeof window !== "undefined" ? window.location.origin : "",
                }}
            />

            {/* HERO */}
            <section className="relative overflow-hidden grain">
                <div className="container-x pt-16 lg:pt-24 pb-20 lg:pb-32 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                    <div className="lg:col-span-7 animate-fade-up">
                        <span className="label-eyebrow inline-flex items-center gap-2">
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-terracotta" />
                            {t("home.hero.eyebrow")}
                        </span>
                        <h1 className="mt-6 text-4xl sm:text-5xl lg:text-[5.5rem] leading-[1.02] tracking-tight font-heading font-medium text-ink">
                            {t("home.hero.title1")}{" "}
                            <span className="italic text-terracotta">{t("home.hero.titleAccent1")}</span>{" "}
                            <span className="italic text-terracotta">{t("home.hero.titleAccent2")}</span>
                        </h1>
                        <p className="mt-7 max-w-xl text-lg text-ink-soft leading-relaxed">
                            {t("home.hero.body")}
                        </p>
                        <div className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                            <Link
                                to="/waitlist"
                                data-testid="hero-homeowner-cta"
                                className="group inline-flex items-center gap-2 rounded-full bg-terracotta text-cream font-semibold px-7 py-4 hover:bg-terracotta-hover transition-all duration-300 hover:scale-[1.02]"
                            >
                                {t("home.hero.ctaHomeowner")}
                                <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </Link>
                            <Link
                                to="/waitlist#provider"
                                data-testid="hero-provider-cta"
                                className="group inline-flex items-center gap-2 rounded-full border border-ink/30 text-ink font-semibold px-7 py-4 hover:bg-ink hover:text-cream hover:border-ink transition-colors duration-300"
                            >
                                {t("home.hero.ctaProvider")}
                                <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </Link>
                        </div>

                        <div className="mt-12 flex items-center gap-6 text-sm text-ink-soft">
                            <div className="flex -space-x-2">
                                <span className="w-9 h-9 rounded-full bg-terracotta/80 border-2 border-cream" />
                                <span className="w-9 h-9 rounded-full bg-sage border-2 border-cream" />
                                <span className="w-9 h-9 rounded-full bg-ink border-2 border-cream" />
                            </div>
                            <p>{t("home.hero.trustText")}</p>
                        </div>
                    </div>

                    <div className="lg:col-span-5 relative animate-fade-in">
                        <div className="relative rounded-3xl overflow-hidden shadow-xl">
                            <img
                                src={heroImg}
                                alt="A warm, beautifully designed cozy living room representing a welcoming home"
                                className="w-full h-[560px] object-cover"
                                loading="eager"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-ink/30 via-transparent to-transparent" />
                        </div>

                        {/* Floating trust badge */}
                        <div className="absolute -bottom-6 -left-6 bg-cream border border-border rounded-2xl shadow-xl p-5 max-w-[240px] hidden sm:block">
                            <div className="flex items-center gap-3">
                                <span className="w-10 h-10 rounded-full bg-terracotta/10 flex items-center justify-center text-terracotta">
                                    <ShieldCheck size={20} />
                                </span>
                                <div>
                                    <p className="text-xs label-eyebrow !text-ink-soft">{t("home.hero.badgeEyebrow")}</p>
                                    <p className="font-heading text-sm text-ink">{t("home.hero.badgeText")}</p>
                                </div>
                            </div>
                        </div>

                        <div className="absolute -top-6 -right-4 bg-ink text-cream rounded-2xl shadow-xl p-5 max-w-[220px] hidden md:block">
                            <p className="label-eyebrow !text-terracotta">{t("home.hero.missionEyebrow")}</p>
                            <p className="mt-2 font-heading text-base leading-snug">
                                {t("home.hero.missionText")}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* WHO WE ARE */}
            <section className="bg-cream-deep">
                <div className="container-x section-y grid lg:grid-cols-12 gap-12 items-start">
                    <div className="lg:col-span-5">
                        <p className="label-eyebrow">{t("home.who.eyebrow")}</p>
                        <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-heading tracking-tight leading-tight text-ink">
                            {t("home.who.title1")}{" "}
                            <span className="italic text-terracotta">{t("home.who.titleAccent")}</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-7 space-y-6 text-lg text-ink-soft leading-relaxed">
                        <p>{t("home.who.body1")}</p>
                        <p>{t("home.who.body2")}</p>
                        <Link
                            to="/about"
                            data-testid="who-we-are-learn-more"
                            className="inline-flex items-center gap-2 text-ink font-semibold link-underline"
                        >
                            {t("home.who.cta")}
                            <ArrowUpRight size={18} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* WHY THIS MATTERS */}
            <section className="bg-cream">
                <div className="container-x section-y">
                    <div className="max-w-3xl">
                        <p className="label-eyebrow">{t("home.why.eyebrow")}</p>
                        <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-heading tracking-tight leading-tight text-ink">
                            {t("home.why.title1")}{" "}
                            <span className="italic text-terracotta">{t("home.why.titleAccent")}</span>
                        </h2>
                    </div>

                    <div className="mt-14 grid md:grid-cols-2 gap-6">
                        <div className="bg-white border border-border rounded-2xl p-8 hover:shadow-md transition-shadow">
                            <span className="w-12 h-12 rounded-full bg-terracotta/10 flex items-center justify-center text-terracotta">
                                <Heart size={22} />
                            </span>
                            <h3 className="mt-6 font-heading text-2xl text-ink">{t("home.why.homeownersHeading")}</h3>
                            <p className="mt-3 text-ink-soft leading-relaxed">
                                {t("home.why.homeownersBody")}
                            </p>
                        </div>
                        <div className="bg-ink text-cream border border-ink rounded-2xl p-8">
                            <span className="w-12 h-12 rounded-full bg-terracotta/20 flex items-center justify-center text-terracotta">
                                <Sparkles size={22} />
                            </span>
                            <h3 className="mt-6 font-heading text-2xl">{t("home.why.providersHeading")}</h3>
                            <p className="mt-3 text-cream/70 leading-relaxed">
                                {t("home.why.providersBody")}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* HOW IT WILL WORK */}
            <section className="bg-cream-deep">
                <div className="container-x section-y">
                    <div className="grid lg:grid-cols-12 gap-8 items-end">
                        <div className="lg:col-span-7">
                            <p className="label-eyebrow">{t("home.how.eyebrow")}</p>
                            <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-heading tracking-tight leading-tight text-ink">
                                {t("home.how.title1")}{" "}
                                <span className="italic text-terracotta">{t("home.how.titleAccent")}</span>
                            </h2>
                        </div>
                        <p className="lg:col-span-5 text-lg text-ink-soft leading-relaxed">
                            {t("home.how.body")}
                        </p>
                    </div>

                    <div className="mt-16 grid md:grid-cols-3 gap-8">
                        {[
                            { n: "01", title: t("home.how.step1Title"), body: t("home.how.step1Body"), Icon: Search },
                            { n: "02", title: t("home.how.step2Title"), body: t("home.how.step2Body"), Icon: MapPin },
                            { n: "03", title: t("home.how.step3Title"), body: t("home.how.step3Body"), Icon: Handshake },
                        ].map(({ n, title, body, Icon }) => (
                            <div key={n} className="relative">
                                <div className="absolute -top-3 left-0 font-heading italic text-6xl text-terracotta/20">{n}</div>
                                <div className="pt-12">
                                    <span className="w-12 h-12 rounded-full bg-cream border border-border flex items-center justify-center text-terracotta">
                                        <Icon size={20} />
                                    </span>
                                    <h3 className="mt-5 font-heading text-xl text-ink">{title}</h3>
                                    <p className="mt-3 text-ink-soft leading-relaxed">{body}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* BENEFITS - Tetris layout */}
            <section className="bg-cream">
                <div className="container-x section-y space-y-24">
                    {/* Homeowner benefits */}
                    <div className="grid lg:grid-cols-12 gap-12 items-center">
                        <div className="lg:col-span-7 order-2 lg:order-1">
                            <p className="label-eyebrow">{t("home.benefits.homeownersEyebrow")}</p>
                            <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-heading tracking-tight leading-tight text-ink">
                                {t("home.benefits.homeownersTitle1")}{" "}
                                <span className="italic text-terracotta">{t("home.benefits.homeownersTitleAccent")}</span>
                            </h2>
                            <ul className="mt-8 space-y-4 text-lg text-ink-soft">
                                {(Array.isArray(homeownerItems) ? homeownerItems : []).map((b) => (
                                    <li key={b} className="flex items-start gap-3">
                                        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-terracotta shrink-0" />
                                        <span>{b}</span>
                                    </li>
                                ))}
                            </ul>
                            <Link
                                to="/for-homeowners"
                                data-testid="benefits-homeowner-cta"
                                className="mt-9 inline-flex items-center gap-2 rounded-full bg-ink text-cream font-semibold px-6 py-3.5 hover:bg-ink-soft transition-colors"
                            >
                                {t("home.benefits.homeownersCta")}
                                <ArrowUpRight size={18} />
                            </Link>
                        </div>
                        <div className="lg:col-span-5 order-1 lg:order-2">
                            <div className="relative rounded-3xl overflow-hidden">
                                <img
                                    src={contractorImg}
                                    alt="A confident woman homeowner enjoying coffee at her kitchen counter beside an open laptop"
                                    className="w-full h-[460px] object-cover"
                                    loading="lazy"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Provider benefits */}
                    <div className="grid lg:grid-cols-12 gap-12 items-center">
                        <div className="lg:col-span-5">
                            <div className="relative rounded-3xl overflow-hidden">
                                <img
                                    src={tradesImg}
                                    alt="A skilled woman service provider in a hard hat planning a renovation with a tablet in hand"
                                    className="w-full h-[460px] object-cover"
                                    loading="lazy"
                                />
                            </div>
                        </div>
                        <div className="lg:col-span-7">
                            <p className="label-eyebrow">{t("home.benefits.providersEyebrow")}</p>
                            <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-heading tracking-tight leading-tight text-ink">
                                {t("home.benefits.providersTitle1")}{" "}
                                <span className="italic text-terracotta">{t("home.benefits.providersTitleAccent")}</span>
                            </h2>
                            <ul className="mt-8 space-y-4 text-lg text-ink-soft">
                                {(Array.isArray(providerItems) ? providerItems : []).map((b) => (
                                    <li key={b} className="flex items-start gap-3">
                                        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-terracotta shrink-0" />
                                        <span>{b}</span>
                                    </li>
                                ))}
                            </ul>
                            <a
                                href={FORMS.foundingProvider}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="benefits-provider-cta"
                                className="mt-9 inline-flex items-center gap-2 rounded-full bg-ink text-cream font-semibold px-6 py-3.5 hover:bg-ink-soft transition-colors"
                            >
                                {t("home.benefits.providersCta")}
                                <ArrowUpRight size={18} />
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* EMAIL SIGNUP CARD */}
            <section className="bg-cream-deep">
                <div className="container-x section-y">
                    <div className="grid lg:grid-cols-12 gap-12 items-center">
                        <div className="lg:col-span-5">
                            <p className="label-eyebrow">{t("home.signup.eyebrow")}</p>
                            <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-heading tracking-tight leading-tight text-ink">
                                {t("home.signup.title1")}{" "}
                                <span className="italic text-terracotta">{t("home.signup.titleAccent")}</span>
                            </h2>
                            <p className="mt-6 text-lg text-ink-soft leading-relaxed">
                                {t("home.signup.body")}
                            </p>
                            <Link
                                to="/waitlist"
                                data-testid="home-community-cta"
                                className="mt-8 inline-flex items-center gap-2 rounded-full bg-terracotta text-cream font-semibold px-7 py-4 hover:bg-terracotta-hover transition-all duration-300 hover:scale-[1.02]"
                            >
                                {t("home.signup.cta")}
                                <ArrowUpRight size={18} />
                            </Link>
                        </div>
                        <div className="lg:col-span-7">
                            <div className="relative rounded-3xl overflow-hidden shadow-sm">
                                <img
                                    src={computerImg}
                                    alt="An open laptop on a cozy table - sign up for the TrustHer Home community from anywhere"
                                    className="w-full h-[460px] object-cover"
                                    loading="lazy"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* COMING SOON BANNER */}
            <section className="relative">
                <div className="container-x py-16">
                    <div className="rounded-3xl bg-terracotta text-cream p-10 lg:p-16 grid lg:grid-cols-12 gap-8 items-center shadow-xl">
                        <div className="lg:col-span-8">
                            <p className="label-eyebrow !text-cream/80">{t("home.comingSoon.eyebrow")}</p>
                            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-heading tracking-tight leading-tight">
                                {t("home.comingSoon.title1")}{" "}
                                <span className="italic">{t("home.comingSoon.titleAccent")}</span>
                            </h2>
                            <p className="mt-5 max-w-xl text-cream/90 leading-relaxed">
                                {t("home.comingSoon.body")}
                            </p>
                        </div>
                        <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3">
                            <a
                                href={FORMS.homeownerCommunity}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="coming-soon-homeowner-cta"
                                className="inline-flex items-center justify-center gap-2 rounded-full bg-cream text-ink font-semibold px-6 py-3.5 hover:bg-ink hover:text-cream transition-colors"
                            >
                                {t("home.comingSoon.ctaHomeowner")}
                                <ArrowUpRight size={18} />
                            </a>
                            <a
                                href={FORMS.foundingProvider}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="coming-soon-provider-cta"
                                className="inline-flex items-center justify-center gap-2 rounded-full border border-cream/40 text-cream font-semibold px-6 py-3.5 hover:bg-cream hover:text-ink transition-colors"
                            >
                                {t("home.comingSoon.ctaProvider")}
                                <ArrowUpRight size={18} />
                            </a>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
